import { questions } from '@bioliberty/core/surveys';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useGetSurveyResponsesQuery } from '../../api/internal';
import { Tile } from '../../components/Tile';
import { useDateRange } from '../../hooks/useDateRange';
import { useTenantIds } from '../../hooks/useTenantIds';
import { getBarColor } from './survey-bar-colors';
import { SurveyResponseBarChart } from './SurveyResponseBarChart';
import { SelectedBar, SurveyResponseDetailDialog } from './SurveyResponseDetailDialog';
import { SurveyResponsesTilesSkeleton } from './SurveyResponsesTilesSkeleton';

export interface SurveyResponsesTilesProps {
  dateRange: [Date, Date] | null;
}

export function SurveyResponsesTiles({ dateRange }: SurveyResponsesTilesProps) {
  const tenantIds = useTenantIds();
  const { dateFrom, dateTo } = useDateRange(dateRange);
  const { data, isLoading } = useGetSurveyResponsesQuery({ tenantIds, dateFrom, dateTo });
  const questionSummaries = data?.questionSummaries ?? {};
  const [selectedBar, setSelectedBar] = useState<SelectedBar | null>(null);

  if (isLoading) {
    return <SurveyResponsesTilesSkeleton />;
  }

  if (!Object.keys(questionSummaries).length) {
    return (
      <Tile title="" titleSize="sm" className="flex items-center">
        <AlertCircle className="me-2 inline w-4" />
        No survey responses to display. Try adjusting your date filter or try again later.
      </Tile>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {Object.entries(questionSummaries).map(([questionId, summary], index) => {
        const question = questions[Number(questionId)];
        if (!question) {
          return null;
        }

        const chartData = Object.entries(summary).map(([answerId, answerSummary]) => {
          const option = question.options.find((o) => o.id === Number(answerId));
          return {
            count: answerSummary.count,
            value: answerSummary.percentage,
            label: option?.text ?? answerId,
            answerValue: Number(answerId)
          };
        });

        return (
          <Tile key={questionId} title={`${index + 1}. ${question?.text ?? ''}`} titleSize="sm">
            <SurveyResponseBarChart
              data={chartData}
              selectedBarIndex={selectedBar?.questionId === Number(questionId) ? selectedBar.barIndex : null}
              onBarClick={(barIndex) => {
                const item = chartData[barIndex];
                if (selectedBar?.questionId === Number(questionId) && selectedBar?.answerValue === item.answerValue) {
                  setSelectedBar(null);
                  return;
                }
                setSelectedBar({
                  questionId: Number(questionId),
                  barIndex,
                  answerValue: item.answerValue,
                  label: item.label,
                  count: item.count,
                  percentage: item.value,
                  color: getBarColor(barIndex)
                });
              }}
            />
          </Tile>
        );
      })}

      {selectedBar && (
        <SurveyResponseDetailDialog
          selectedBar={selectedBar}
          tenantIds={tenantIds}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={() => setSelectedBar(null)}
        />
      )}
    </div>
  );
}
